import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image, Link } from '@react-pdf/renderer';
import Html from 'react-pdf-html';
import { roomLineTotal, nightlyDisplay, occupancyLabel, offerDiscount, discountLabel, formatMoneyTR } from '../../utils/pricing';
import { offerPdfFileName } from '../../utils/offerShare';
import { CANCELLATION_POLICY } from './cancellationPolicy';

// Türkçe karakter desteği için font kaydı
Font.register({
  family: 'Roboto',
  src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
  fontWeight: 'normal'
});
Font.register({
  family: 'Roboto',
  src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
  fontWeight: 'bold'
});

// PDF Stilleri
// Organizasyon teklifi PDF'i (modules/events/EventPDF.js) aynı görünüm için bunları kullanır
export const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    padding: 0,
    fontFamily: 'Roboto', // Türkçe karakterler için Roboto fontu
    fontSize: 7.5,
    color: '#374151',
    backgroundColor: 'transparent',
  },
  contentContainer: {
    paddingHorizontal: 30,
    paddingTop: 15,
    paddingBottom: 10,
    flex: 1,
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    width: '100%',
    height: '100.5%',
    opacity: 1,
    objectFit: 'cover',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 5,
    paddingBottom: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: '#9CA3AF',
  },
  logo: {
    width: 120,
    height: 50,
    objectFit: 'contain',
  },
  headerRight: {
    alignItems: 'flex-end',
  },
  title: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 2,
    textTransform: 'uppercase',
    textAlign: 'right',
  },
  subtitle: {
    fontSize: 7.5,
    color: '#6B7280',
  },
  section: {
    marginBottom: 10,
    paddingBottom: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: '#E5E7EB',
  },
  sectionTitle: {
    fontSize: 8.5,
    fontWeight: 'bold',
    color: '#111827',
    textTransform: 'uppercase',
    marginBottom: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: '#E5E7EB',
    paddingBottom: 4,
    marginTop: 4,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: '#F9FAFB',
    paddingBottom: 2,
  },
  label: {
    width: '30%',
    fontSize: 7.5,
    color: '#4B5563',
    fontWeight: 'bold',
  },
  value: {
    width: '70%',
    fontSize: 7.5,
    color: '#111827',
  },
  table: {
    marginTop: 5,
    borderWidth: 0.5,
    borderColor: '#E5E7EB',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F3F4F6',
    padding: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: '#E5E7EB',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: '#E5E7EB',
  },
  tableCellName: {
    width: '70%',
    fontSize: 7.5,
  },
  tableCellType: {
    width: '30%',
    fontSize: 7.5,
    color: '#6B7280',
  },
  totalSection: {
    marginTop: 5,
    paddingTop: 5,
    borderTopWidth: 0.5,
    borderTopColor: '#9CA3AF',
    alignItems: 'flex-start', // Sola hizalı veya sütun içinde
  },
  htmlContainer: {
    fontSize: 7.5,
    color: '#374151',
  },
  totalLabel: {
    fontSize: 10.5,
    color: '#374151',
    marginBottom: 2,
  },
  totalValue: {
    fontSize: 10.5,
    fontWeight: 'bold',
    color: '#111827',
  },
  // Üstteki bölümün (styles.section) alt çizgisi ayırıcıdır. Eskiden burada ikinci bir çizgi + 20pt boşluk
  // vardı; indirim satırlarına 1. sayfada yer açmak için kaldırıldı (2026-09-19).
  bankSection: {
    flexDirection: 'row',
  },
  bankTitle: {
    fontSize: 8.5,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 2,
    textTransform: 'uppercase',
  },
  bankRow: {
    flexDirection: 'row',
    marginBottom: 1,
  },
  bankLabel: {
    width: 60,
    fontSize: 6.5,
    color: '#4B5563',
    fontWeight: 'bold',
  },
  bankValue: {
    flex: 1,
    fontSize: 6.5,
    color: '#111827',
  },
  bankColumnLeft: {
    flex: 1,
    paddingRight: 10,
  },
  bankColumnRight: {
    flex: 1,
    paddingLeft: 10,
    borderLeftWidth: 0.5,
    borderLeftColor: '#E5E7EB',
  },
  policyText: {
    fontSize: 6.5,
    color: '#374151',
    lineHeight: 1.4,
    textAlign: 'justify',
  },
  footer: {
    position: 'absolute',
    bottom: 10,
    left: 10,
    right: 10,
    borderTopWidth: 0.5,
    borderTopColor: '#E5E7EB',
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerLeft: {
    flex: 1,
  },
  footerText: {
    fontSize: 7.5,
    color: '#000000',
  },
  footerRight: {
    alignItems: 'center',
    marginLeft: 10,
  },
  qrCode: {
    width: 40,
    height: 40,
  },
  roomsButtonContainer: {
    position: 'absolute',
    bottom: 30,
    right: 30,
  },
  roomsButtonText: {
    backgroundColor: '#DC2626', // red-600
    color: '#FFFFFF',
    fontSize: 9,
    fontWeight: 'bold',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 5,
  },
});

// HTML İçerik Stilleri (Oda Özellikleri için)
export const htmlStylesheet = {
  p: { fontSize: 7.5, fontFamily: 'Roboto', marginBottom: 2 },
  li: { fontSize: 7.5, fontFamily: 'Roboto', marginBottom: 2 },
  ul: { margin: 0, paddingLeft: 15 },
  ol: { margin: 0, paddingLeft: 15 },
  h1: { fontSize: 8.5, fontWeight: 'bold', marginBottom: 2 },
  h2: { fontSize: 8.5, fontWeight: 'bold', marginBottom: 2 },
  h3: { fontSize: 8.5, fontWeight: 'bold', marginBottom: 2 },
  strong: { fontWeight: 'bold' },
  b: { fontWeight: 'bold' },
  em: { fontStyle: 'italic' },
  i: { fontStyle: 'italic' },
};

// ─── Son sayfa: Rezervasyon, ödeme ve iptal koşulları ──────────────────────────
// Metin src/modules/offers/cancellationPolicy.js'te; burada yalnızca düzen var.
// Tasarım bilinçli olarak SADE (proje sahibi isteği): 1. sayfanın bölüm başlıkları, ince gri çizgiler,
// siyah/gri metin — renkli kart, rozet, çubuk yok.
// Antetli arka planın alt ~%15'i otel fotoğrafı (≈714pt'den itibaren): içerik bu bandın üstünde bitmeli.

export const policyStyles = StyleSheet.create({
  headerRef: {
    fontSize: 7.5,
    color: '#9CA3AF',
    marginTop: 1,
  },
  // Boşluklar sıkı: metin 2026-09-19'da uzadı, fotoğraf bandına taşmasın diye (yazı boyutları korunuyor)
  section: {
    marginBottom: 7,
  },
  body: {
    fontSize: 8.5,
    color: '#374151',
    lineHeight: 1.4,
  },
  table: {
    borderWidth: 0.5,
    borderColor: '#E5E7EB',
    marginTop: 2,
  },
  tableHead: {
    flexDirection: 'row',
    backgroundColor: '#F3F4F6',
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: '#E5E7EB',
  },
  tableHeadText: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#111827',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 2.5,
    paddingHorizontal: 8,
    borderTopWidth: 0.5,
    borderTopColor: '#F3F4F6',
  },
  colWhen: {
    width: '60%',
    fontSize: 8.5,
    color: '#374151',
  },
  colDeduction: {
    width: '40%',
    fontSize: 8.5,
    color: '#111827',
    fontWeight: 'bold',
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 2,
  },
  bullet: {
    width: 10,
    fontSize: 8.5,
    color: '#6B7280',
  },
  listText: {
    flex: 1,
    fontSize: 8.5,
    color: '#374151',
    lineHeight: 1.4,
  },
  legalBullet: {
    width: 10,
    fontSize: 7.5,
    color: '#9CA3AF',
  },
  legalText: {
    flex: 1,
    fontSize: 7.5,
    color: '#4B5563',
    lineHeight: 1.4,
    textAlign: 'justify',
  },
});

const formatOfferDate = (offer) =>
  new Date(offer?.createdAt || Date.now()).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });

const CancellationPolicyPage = ({ offer, settings }) => {
  const policy = CANCELLATION_POLICY;
  const hotelName = settings?.hotelName || 'Ramada by Wyndham Erzincan Ergan';
  const offerRef = [offer?.customerName, `Teklif Tarihi: ${formatOfferDate(offer)}`].filter(Boolean).join('  ·  ');

  return (
    <Page size="A4" style={styles.page}>
      {settings?.pdfBackground && (
        <Image src={settings.pdfBackground} style={styles.backgroundImage} fixed />
      )}

      <View style={styles.contentContainer}>
        {/* Başlık — 1. sayfadaki başlıkla aynı yapı */}
        <View style={styles.header}>
          <View>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.title}>{policy.titleLines.join('\n')}</Text>
            <Text style={styles.subtitle}>{hotelName}</Text>
            <Text style={policyStyles.headerRef}>{offerRef}</Text>
          </View>
        </View>

        {/* Ödeme planı */}
        <View style={policyStyles.section}>
          <Text style={styles.sectionTitle}>{policy.headings.paymentPlan}</Text>
          {policy.paymentPlan.paragraphs.map((text, i) => (
            <Text key={i} style={i > 0 ? [policyStyles.body, { marginTop: 3 }] : policyStyles.body}>{text}</Text>
          ))}
        </View>

        {/* Kesinti tablosu */}
        <View style={policyStyles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{policy.headings.deductionTable}</Text>
          <View style={policyStyles.table}>
            <View style={policyStyles.tableHead}>
              <Text style={[policyStyles.tableHeadText, { width: '60%' }]}>{policy.deductionTable.columns[0]}</Text>
              <Text style={[policyStyles.tableHeadText, { width: '40%' }]}>{policy.deductionTable.columns[1]}</Text>
            </View>
            {policy.deductionTable.rows.map((row) => (
              <View key={row.when} style={policyStyles.tableRow}>
                <Text style={policyStyles.colWhen}>{row.when}</Text>
                <Text style={policyStyles.colDeduction}>{row.deduction}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* İptal koşulları */}
        <View style={policyStyles.section}>
          <Text style={styles.sectionTitle}>{policy.headings.conditions}</Text>
          {policy.conditions.map((text, i) => (
            <View key={i} style={policyStyles.listItem} wrap={false}>
              <Text style={policyStyles.bullet}>•</Text>
              <Text style={policyStyles.listText}>{text}</Text>
            </View>
          ))}
        </View>

        {/* Yasal bilgilendirme */}
        <View>
          <Text style={styles.sectionTitle}>{policy.headings.legalNotice}</Text>
          {policy.legalNotice.map((text, i) => (
            <View key={i} style={policyStyles.listItem} wrap={false}>
              <Text style={policyStyles.legalBullet}>•</Text>
              <Text style={policyStyles.legalText}>{text}</Text>
            </View>
          ))}
        </View>
      </View>
    </Page>
  );
};

// QR koddaki link = OfferForm'un yüklediği PDF'in adı (tek kaynak: utils/offerShare.js)
const getPdfFileName = offerPdfFileName;

export const OfferPDF = ({ offer, settings }) => {
  // Teklif indirimi (yoksa amount 0 → özet bloğu basılmaz; eski tekliflerin görünümü değişmez)
  const discount = offerDiscount(offer);
  const currency = offer.currency || 'TL';
  const money = (n) => `${formatMoneyTR(n)} ${currency}`;

  const defaultImportantNote = `<p><strong>Önemli Not: </strong>Teklif otel müsaitlik durumuna göre değişiklik gösterebilir. <strong>Oda Giriş Saati: </strong>14:00 <strong>Oda Çıkış Saati: </strong>12:00'dır.</p>`;
  const importantNote = settings?.bank?.importantNote !== undefined ? settings.bank.importantNote : defaultImportantNote;
  const hasBankInfo = !!(settings?.bank && (settings.bank.bankName || settings.bank.accountHolder || settings.bank.tlIban || settings.bank.euroIban || settings.bank.usdIban));

  return (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Arka Plan Resmi */}
      {settings?.pdfBackground && (
        <Image
          src={settings.pdfBackground}
          style={styles.backgroundImage}
          fixed
        />
      )}

      <View style={styles.contentContainer}>
      {/* Başlık ve Logo */}
      <View style={styles.header}>
        <View>
        </View>
        <View style={styles.headerRight}>
            <Text style={styles.title}>ÖN REZERVASYON FİYAT VE{'\n'}DETAY BİLGİ FORMU</Text>
            <Text style={styles.subtitle}>
              Teklif Tarihi: {offer.createdAt ? new Date(offer.createdAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </Text>
        </View>
      </View>

      {/* Müşteri ve Otel Bilgileri */}
      <View style={styles.section}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            {/* Sol: Müşteri Bilgileri */}
            <View style={{ width: '38%' }}>
                <Text style={styles.sectionTitle}>MÜŞTERİ BİLGİLERİ</Text>
                <View style={styles.row}>
                  <Text style={styles.label}>Ad Soyad:</Text>
                  <Text style={styles.value}>{offer.customerName}</Text>
                </View>
                {offer.phone && (
                  <View style={styles.row}>
                    <Text style={styles.label}>Telefon:</Text>
                    <Text style={styles.value}>{offer.phone}</Text>
                  </View>
                )}
                {offer.email && (
                  <View style={styles.row}>
                    <Text style={styles.label}>E-posta:</Text>
                    <Text style={styles.value}>{offer.email}</Text>
                  </View>
                )}
            </View>

            {/* Orta: Otel Bilgileri */}
            <View style={{ width: '37%', marginTop: 5, paddingLeft: 10 }}>
                <Text style={{ fontSize: 9.5, fontWeight: 'bold', color: '#111827', marginBottom: 6 }}>
                    {settings?.hotelName || 'Ramada by Wyndham Ergan Kayak Merkezi'}
                </Text>
                <View style={{ flexDirection: 'row', marginBottom: 4 }}>
                    <Text style={{ width: '30%', fontSize: 8.5, color: '#4B5563', fontWeight: 'bold' }}>Telefon:</Text>
                    <Text style={{ width: '70%', fontSize: 8.5, color: '#111827' }}>{settings?.hotelPhone || '0547 811 24 24'}</Text>
                </View>
                <View style={{ flexDirection: 'row', marginBottom: 4 }}>
                    <Text style={{ width: '30%', fontSize: 8.5, color: '#4B5563', fontWeight: 'bold' }}>E-Posta:</Text>
                    <Text style={{ width: '70%', fontSize: 8.5, color: '#111827' }}>{settings?.hotelEmail || 'info@ramadaergan.com'}</Text>
                </View>
            </View>

            {/* Sağ: QR Kod */}
            <View style={{ width: '25%', alignItems: 'flex-end', justifyContent: 'center', marginTop: 5 }}>
                <Image
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent('https://crm.ramadaergan.com/offers/view/' + getPdfFileName(offer))}`}
                    style={{ width: 60, height: 60 }}
                />
                <Text style={{ fontSize: 6.5, color: '#9CA3AF', marginTop: 2, textAlign: 'center', width: 60 }}>Online Görüntüle</Text>
            </View>
        </View>
      </View>

      {/* Konaklama Detayları */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Konaklama Detayları</Text>
        
        {/* Başlık Satırı */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderBottomWidth: 0.5, borderBottomColor: '#E5E7EB', paddingBottom: 4, marginBottom: 4 }}>
            <Text style={{ flex: 3, fontSize: 9.5, color: '#111827', fontWeight: 'bold' }}>Oda Tipi</Text>
            <Text style={{ flex: 2.5, fontSize: 9.5, color: '#111827', fontWeight: 'bold' }}>Giriş - Çıkış</Text>
            <Text style={{ flex: 0.7, fontSize: 9.5, color: '#111827', fontWeight: 'bold', textAlign: 'center' }}>Süre</Text>
            <Text style={{ flex: 1, fontSize: 9.5, color: '#111827', fontWeight: 'bold', textAlign: 'right' }}>Gecelik</Text>
            <Text style={{ flex: 1, fontSize: 9.5, color: '#111827', fontWeight: 'bold', textAlign: 'right' }}>Toplam</Text>
        </View>

        {/* Oda Listesi */}
        {offer.rooms && offer.rooms.length > 0 ? (
            offer.rooms.map((room, index) => (
                <View key={index} style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4, paddingBottom: 4, borderBottomWidth: 0.5, borderBottomColor: '#F9FAFB' }}>
                    <View style={{ flex: 3 }}>
                        <Text style={{ fontSize: 9.5, color: '#111827' }}>{room.name}</Text>
                        <Text style={{ fontSize: 7.5, color: '#6B7280', marginTop: 1 }}>
                            ({occupancyLabel(room)})
                        </Text>
                    </View>
                    <View style={{ flex: 2.5 }}>
                        <Text style={{ fontSize: 9.5, color: '#111827' }}>
                            {room.checkIn ? new Date(room.checkIn).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'} / {room.checkOut ? new Date(room.checkOut).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                        </Text>
                    </View>
                    <View style={{ flex: 0.7 }}>
                        <Text style={{ fontSize: 9.5, color: '#111827', textAlign: 'center' }}>{room.nights} Gece</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                        {/* Geceler farklı fiyatlıysa ortalama gösterilir ("ort."); toplam her zaman gerçek toplamdır */}
                        <Text style={{ fontSize: 9.5, color: '#111827', textAlign: 'right' }}>
                            {nightlyDisplay(room).value.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} {currency}
                        </Text>
                        {nightlyDisplay(room).isAverage && (
                            <Text style={{ fontSize: 7, color: '#6b7280', textAlign: 'right' }}>ort.</Text>
                        )}
                    </View>
                    <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: 9.5, color: '#111827', fontWeight: 'bold', textAlign: 'right' }}>{roomLineTotal(room).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} {currency}</Text>
                    </View>
                </View>
            ))
        ) : (
            // Eski kayıtlar için uyumluluk (Tek oda)
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                <View style={{ flex: 3 }}>
                    <Text style={{ fontSize: 9.5, color: '#111827' }}>{offer.room?.name}</Text>
                    <Text style={{ fontSize: 7.5, color: '#6B7280', marginTop: 1 }}>
                        ({offer.adults || 1} Yetişkin, {offer.children || 0} Çocuk)
                    </Text>
                </View>
                <View style={{ flex: 2.5 }}>
                    <Text style={{ fontSize: 9.5, color: '#111827' }}>
                        {offer.checkIn ? new Date(offer.checkIn).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'} / {offer.checkOut ? new Date(offer.checkOut).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                    </Text>
                </View>
                <View style={{ flex: 0.7 }}>
                    <Text style={{ fontSize: 9.5, color: '#111827', textAlign: 'center' }}>{offer.nights} Gece</Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 9.5, color: '#111827', textAlign: 'right' }}>-</Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 9.5, color: '#111827', fontWeight: 'bold', textAlign: 'right' }}>{offer.totalPrice?.toLocaleString('tr-TR')} {currency}</Text>
                </View>
            </View>
        )}

        {/* Ara toplam · indirim · toplam — yalnızca indirimli tekliflerde */}
        {discount.amount > 0 && (
            <View style={{ paddingTop: 3, borderTopWidth: 0.5, borderTopColor: '#E5E7EB' }} wrap={false}>
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 1 }}>
                    <Text style={{ fontSize: 8, color: '#4B5563', textAlign: 'right', paddingRight: 10 }}>Ara Toplam</Text>
                    <Text style={{ fontSize: 8, color: '#4B5563', width: 95, textAlign: 'right' }}>{money(discount.subTotal)}</Text>
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 2 }}>
                    {/* Kırmızı + kalın (proje sahibi isteği 2026-10-03): indirim satırı dikkat çeksin */}
                    <Text style={{ fontSize: 8, color: '#B91C1C', fontWeight: 'bold', textAlign: 'right', paddingRight: 10 }}>{discountLabel(discount)}</Text>
                    <Text style={{ fontSize: 8, color: '#B91C1C', fontWeight: 'bold', width: 95, textAlign: 'right' }}>-{money(discount.amount)}</Text>
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                    <Text style={{ fontSize: 9.5, color: '#111827', fontWeight: 'bold', textAlign: 'right', paddingRight: 10 }}>Toplam</Text>
                    <Text style={{ fontSize: 9.5, color: '#111827', fontWeight: 'bold', width: 95, textAlign: 'right' }}>{money(discount.total)}</Text>
                </View>
            </View>
        )}
      </View>

      {/* Oda Özellikleri ve Otel Konsepti */}
      {(offer.room?.features || settings?.hotelConcept) && (
        <View style={styles.section}>
            <View style={{ flexDirection: 'row' }}>
                {offer.room?.features && (
                    <View style={{ flex: 1, paddingRight: settings?.hotelConcept ? 10 : 0 }}>
                        <Text style={styles.sectionTitle}>ODA ÖZELLİKLERİ</Text>
                        <View style={styles.htmlContainer}>
                            <Html stylesheet={htmlStylesheet}>{offer.room.features}</Html>
                        </View>
                    </View>
                )}
                {settings?.hotelConcept && (
                    <View style={{ flex: 1, paddingLeft: offer.room?.features ? 10 : 0, borderLeftWidth: offer.room?.features ? 0.5 : 0, borderLeftColor: '#E5E7EB' }}>
                        <Text style={styles.sectionTitle}>OTEL KONSEPTİ</Text>
                        <View style={styles.htmlContainer}>
                            <Html stylesheet={htmlStylesheet}>{settings.hotelConcept}</Html>
                        </View>
                    </View>
                )}
            </View>
        </View>
      )}

      {/* Banka Bilgileri + Önemli Not / İptal koşulları yönlendirmesi (her zaman görünür) */}
      <View style={styles.bankSection}>
            {/* Sol Kolon: Banka Bilgileri */}
            {hasBankInfo && (
            <View style={styles.bankColumnLeft}>
                <Text style={styles.bankTitle}>BANKA HESAP BİLGİLERİ</Text>
                {settings.bank.bankName && (
                    <View style={styles.bankRow}>
                        <Text style={styles.bankLabel}>Banka:</Text>
                        <Text style={styles.bankValue}>{settings.bank.bankName}</Text>
                    </View>
                )}
                {settings.bank.accountHolder && (
                    <View style={styles.bankRow}>
                        <Text style={styles.bankLabel}>Hesap Sahibi:</Text>
                        <Text style={styles.bankValue}>{settings.bank.accountHolder}</Text>
                    </View>
                )}
                {settings.bank.tlIban && (
                    <View style={styles.bankRow}>
                        <Text style={styles.bankLabel}>TL IBAN:</Text>
                        <Text style={styles.bankValue}>{settings.bank.tlIban}</Text>
                    </View>
                )}
                {settings.bank.euroIban && (
                    <View style={styles.bankRow}>
                        <Text style={styles.bankLabel}>EURO IBAN:</Text>
                        <Text style={styles.bankValue}>{settings.bank.euroIban}</Text>
                    </View>
                )}
                {settings.bank.usdIban && (
                    <View style={styles.bankRow}>
                        <Text style={styles.bankLabel}>USD IBAN:</Text>
                        <Text style={styles.bankValue}>{settings.bank.usdIban}</Text>
                    </View>
                )}
            </View>
            )}

            {/* Sağ Kolon: Önemli Not + İptal Koşulları yönlendirmesi.
                settings.bank.cancellationPolicy (eski "iadesiz" metni) artık basılmıyor —
                geçerli koşullar son sayfada (cancellationPolicy.js). */}
            <View style={hasBankInfo ? styles.bankColumnRight : { flex: 1 }}>
                {importantNote ? (
                    <View style={{ marginBottom: 4 }}>
                        <Html stylesheet={htmlStylesheet}>{importantNote}</Html>
                    </View>
                ) : null}
                <Text style={styles.bankTitle}>İptal ve İade Koşulları</Text>
                <Text style={[styles.policyText, { fontSize: 7.5 }]}>{CANCELLATION_POLICY.page1Reference}</Text>
            </View>
      </View>

      {/* Mail Order Formu */}
      <View style={{ marginTop: 10, paddingTop: 10, borderTopWidth: 0.5, borderTopColor: '#E5E7EB' }}>
        <Text style={styles.bankTitle}>MAIL ORDER FORMU</Text>
        <View style={{ borderWidth: 0.5, borderColor: '#E5E7EB', padding: 8 }}>
            
            {/* 1. Satır: Kart Sahibi ve Kart No */}
            <View style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-end' }}>
                <View style={{ flex: 1, paddingRight: 15 }}>
                    <Text style={{ fontSize: 7.5, color: '#4B5563', marginBottom: 4 }}>Kart Sahibi Adı Soyadı</Text>
                    <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#9CA3AF', height: 16 }} />
                </View>
                <View style={{ flex: 1.5 }}>
                    <Text style={{ fontSize: 7.5, color: '#4B5563', marginBottom: 4 }}>Kart No</Text>
                    <View style={{ flexDirection: 'row' }}>
                        {[...Array(16)].map((_, i) => (
                            <View key={i} style={{ 
                                width: 15, 
                                height: 18, 
                                borderWidth: 1, 
                                borderColor: '#374151', 
                                marginRight: (i + 1) % 4 === 0 && i !== 15 ? 4 : 1 
                            }} />
                        ))}
                    </View>
                </View>
            </View>

            {/* 2. Satır: Ay/Yıl, CVV, Ödeme Tarihi */}
            <View style={{ flexDirection: 'row', marginBottom: 10 }}>
                <View style={{ width: '20%', paddingRight: 15 }}>
                    <Text style={{ fontSize: 7.5, color: '#4B5563', marginBottom: 4 }}>Kart Ay / Yıl</Text>
                    <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#9CA3AF', height: 16 }} />
                </View>
                <View style={{ width: '15%', paddingRight: 15 }}>
                    <Text style={{ fontSize: 7.5, color: '#4B5563', marginBottom: 4 }}>CVV</Text>
                    <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#9CA3AF', height: 16 }} />
                </View>
                <View style={{ width: '25%', paddingRight: 15 }}>
                    <Text style={{ fontSize: 7.5, color: '#4B5563', marginBottom: 4 }}>Ödeme Tarihi</Text>
                    <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#9CA3AF', height: 16 }} />
                </View>
                <View style={{ width: '40%' }}>
                    <Text style={{ fontSize: 7.5, color: '#4B5563', marginBottom: 4 }}>Banka İsmi</Text>
                    <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#9CA3AF', height: 16 }} />
                </View>
            </View>

            {/* 3. Satır: Tutar (Rakam), Tutar (Yazı), İmza */}
            <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
                <View style={{ width: '25%', paddingRight: 15 }}>
                    <Text style={{ fontSize: 7.5, color: '#4B5563', marginBottom: 4 }}>Ödeme Tutarı (Rakamla)</Text>
                    <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#9CA3AF', height: 16 }} />
                </View>
                <View style={{ width: '45%', paddingRight: 15 }}>
                    <Text style={{ fontSize: 7.5, color: '#4B5563', marginBottom: 4 }}>Ödeme Tutarı (Yazıyla)</Text>
                    <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#9CA3AF', height: 16 }} />
                </View>
                <View style={{ width: '30%' }}>
                    <Text style={{ fontSize: 7.5, color: '#4B5563', marginBottom: 4 }}>İmza: Rezervasyon ve Ödeme Onayı</Text>
                    <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#9CA3AF', height: 24 }} />
                </View>
            </View>
        </View>
        {settings?.bank?.mailOrderInfo && (
            <Text style={{ fontSize: 6.5, color: '#374151', marginTop: 4, textAlign: 'justify', lineHeight: 1.4 }}>{settings.bank.mailOrderInfo}</Text>
        )}
      </View>
      </View>

      {/* Footer */}

      {/* Odalarımız Butonu */}
      {settings?.roomsLink && (
        <View style={styles.roomsButtonContainer}>
          <Link src={settings.roomsLink} style={{ textDecoration: 'none' }}>
            <Text style={styles.roomsButtonText}>ODALARIMIZ</Text>
          </Link>
        </View>
      )}
    </Page>

    {/* 2. sayfa: Rezervasyon, ödeme ve iptal koşulları (tüm tekliflerde) */}
    <CancellationPolicyPage offer={offer} settings={settings} />
  </Document>
  );
};